import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RoadmapModuleVisual } from './roadmapModuleIcons';
import './Roadmap.css';

type RoadmapStep = {
  id: string;
  moduleLabel: string;
  title: string;
  phase: string;
  shortDescription: string;
  detail: string;
  tasks: string[];
  isFree: boolean;
  /** Optional brand mark via Clearbit logo API (`logo.clearbit.com/{domain}`) — falls back to Lucide on error */
  logoUrl?: string;
};

/** Order follows a typical deferred-MBA arc: orient → narrative → exam → schools → stories → assets → writing → recommendations → portals → submit → interviews */
const ROADMAP_STEPS: RoadmapStep[] = [
  {
    id: 'todo-list',
    moduleLabel: 'Module 1',
    title: 'To-Do List',
    phase: 'Foundations',
    shortDescription: 'Map your application checklist.',
    detail: 'Set your operating checklist so every requirement is visible and nothing slips between rounds.',
    tasks: ['List each deliverable', 'Assign due dates', 'Track weekly completion'],
    isFree: true
  },
  {
    id: 'career-vision',
    moduleLabel: 'Module 2',
    title: 'Career Vision',
    phase: 'Foundations',
    shortDescription: 'Define your long-term story.',
    detail: 'Clarify your goals, why MBA rationale, and leadership direction so your application has a clear through-line.',
    tasks: ['Draft goals statement', 'Define leadership themes', 'Set post-MBA direction'],
    isFree: true
  },
  {
    id: 'test-strategy',
    moduleLabel: 'Module 3',
    title: 'GMAT / GRE Strategy',
    phase: 'Preparation',
    shortDescription: 'Plan your exam timeline and score goals.',
    detail:
      'Align your test date with application rounds and build a measurable prep cadence you can track week over week.',
    tasks: ['Pick exam type & date', 'Run a baseline diagnostic', 'Set weekly study targets'],
    isFree: true,
    logoUrl: 'https://logo.clearbit.com/gmac.com'
  },
  {
    id: 'target-schools',
    moduleLabel: 'Module 4',
    title: 'Target Schools',
    phase: 'School Strategy',
    shortDescription: 'Prioritize your school list.',
    detail: 'Choose and rank target schools based on fit, selectivity, and your strongest competitive angles.',
    tasks: ['Set school tiers', 'Define school-specific fit', 'Finalize application order'],
    isFree: true
  },
  {
    id: 'school-research',
    moduleLabel: 'Module 5',
    title: 'School Deep Dive',
    phase: 'School Strategy',
    shortDescription: 'Go beyond rankings to real program fit.',
    detail:
      'Research curriculum, culture, clubs, and outcomes so your school list and essays stay specific and credible.',
    tasks: ['Campus or student conversations', 'Course & club fit notes', 'Refresh school tiers'],
    isFree: false,
    logoUrl: 'https://logo.clearbit.com/mba.com'
  },
  {
    id: 'brainstorming',
    moduleLabel: 'Module 6',
    title: 'Brainstorming',
    phase: 'Story Building',
    shortDescription: 'Collect your strongest examples.',
    detail: 'Build your story bank across leadership, impact, teamwork, and resilience to use across essays and interviews.',
    tasks: ['List 8-10 candidate stories', 'Tag by theme', 'Select top anchor stories'],
    isFree: false
  },
  {
    id: 'resume',
    moduleLabel: 'Module 7',
    title: 'Resume',
    phase: 'Application Assets',
    shortDescription: 'Show measurable impact.',
    detail: 'Refine your resume with results-focused bullets and leadership evidence tailored to deferred MBA review.',
    tasks: ['Quantify key bullets', 'Highlight ownership', 'Align with narrative'],
    isFree: false
  },
  {
    id: 'essays',
    moduleLabel: 'Module 8',
    title: 'Essays',
    phase: 'Writing',
    shortDescription: 'Write sharp, school-specific responses.',
    detail: 'Turn your goals and story bank into clear, compelling essays that directly answer each prompt.',
    tasks: ['Build prompt outlines', 'Draft each essay', 'Run revision passes'],
    isFree: false
  },
  {
    id: 'letters-of-recommendation',
    moduleLabel: 'Module 9',
    title: 'Letters of Recommendation',
    phase: 'Supporting Documents',
    shortDescription: 'Guide recommenders effectively.',
    detail: 'Pick recommenders and provide clear context so letters reinforce your core strengths and trajectory.',
    tasks: ['Select recommenders', 'Share talking points', 'Set reminder timeline'],
    isFree: false
  },
  {
    id: 'application-forms',
    moduleLabel: 'Module 10',
    title: 'Application Forms',
    phase: 'Supporting Documents',
    shortDescription: 'Biographical sections and app data.',
    detail:
      'Complete forms consistently with your resume and essays—employment matrix, activities, and short answers.',
    tasks: ['Audit work history fields', 'Activities & awards', 'Cross-check against resume'],
    isFree: false,
    logoUrl: 'https://logo.clearbit.com/liaisonedu.com'
  },
  {
    id: 'final-review',
    moduleLabel: 'Module 11',
    title: 'Final Review & Submit',
    phase: 'Execution',
    shortDescription: 'Structured checklist before submit.',
    detail:
      'Run a final pass across documents, recommendations status, and deadlines before you submit each application.',
    tasks: ['School-by-school checklist', 'Deadline calendar', 'Submission confirmations'],
    isFree: false,
    logoUrl: 'https://logo.clearbit.com/notion.so'
  },
  {
    id: 'interview-prep',
    moduleLabel: 'Module 12',
    title: 'Interview Prep',
    phase: 'Execution',
    shortDescription: 'Practice concise, structured answers.',
    detail: 'Prepare for behavioral and school-fit interviews with answer frameworks tied to your application narrative.',
    tasks: ['Build question bank', 'Practice 2 mock rounds', 'Refine concise examples'],
    isFree: false
  }
];

const getRoadmapStorageKey = (userId: string | undefined) => `deferredly.roadmap.progress.${userId ?? 'guest'}`;

const Roadmap: React.FC = () => {
  const { user } = useAuth();
  const [selectedStepId, setSelectedStepId] = useState<string>(ROADMAP_STEPS[0].id);
  const [completedStepIds, setCompletedStepIds] = useState<string[]>([]);
  const [hasProOverride, setHasProOverride] = useState(false);

  const userHasProFromMetadata = useMemo(() => {
    const plan = user?.user_metadata?.plan;
    const tier = user?.user_metadata?.subscription_tier;
    const status = user?.user_metadata?.subscription_status;
    return plan === 'pro' || tier === 'pro' || status === 'active_paid';
  }, [user]);

  const hasProAccess = userHasProFromMetadata || hasProOverride;

  useEffect(() => {
    const raw = window.localStorage.getItem('deferredly.proAccess');
    setHasProOverride(raw === 'true');
  }, []);

  useEffect(() => {
    const storageKey = getRoadmapStorageKey(user?.id);
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      setCompletedStepIds([]);
      return;
    }

    try {
      const parsed = JSON.parse(raw) as { completedStepIds?: string[] };
      setCompletedStepIds(Array.isArray(parsed.completedStepIds) ? parsed.completedStepIds : []);
    } catch {
      setCompletedStepIds([]);
    }
  }, [user?.id]);

  useEffect(() => {
    const storageKey = getRoadmapStorageKey(user?.id);
    window.localStorage.setItem(storageKey, JSON.stringify({ completedStepIds }));
  }, [completedStepIds, user?.id]);

  const selectedStep = useMemo(
    () => ROADMAP_STEPS.find((step) => step.id === selectedStepId) ?? ROADMAP_STEPS[0],
    [selectedStepId]
  );

  const completedSet = useMemo(() => new Set(completedStepIds), [completedStepIds]);

  const totalSteps = ROADMAP_STEPS.length;
  const completionPct = Math.round((completedSet.size / Math.max(totalSteps, 1)) * 100);

  const isStepLocked = (step: RoadmapStep) => !step.isFree && !hasProAccess;

  const toggleCompleted = (stepId: string) => {
    const step = ROADMAP_STEPS.find((item) => item.id === stepId);
    if (!step || isStepLocked(step)) return;

    setCompletedStepIds((prev) =>
      prev.includes(stepId) ? prev.filter((id) => id !== stepId) : [...prev, stepId]
    );
  };

  return (
    <main className="roadmap-page">
      <div className="roadmap-container">
        <header className="roadmap-header">
          <h1 className="roadmap-title">Application Roadmap</h1>
          <p className="roadmap-subtitle">
            Navigate your deferred MBA application with a guided step-by-step path.
          </p>
          <div className="roadmap-status-callout" role="status" aria-live="polite">
            <span>
              Progress: <strong>{completedSet.size}</strong> / {totalSteps} steps ({completionPct}%)
            </span>
            {!hasProAccess && (
              <span>
                You are on the free track. Paid modules are visible but locked.
              </span>
            )}
          </div>
        </header>

        <section className="roadmap-module-list" aria-label="Roadmap modules">
          {ROADMAP_STEPS.map((step) => {
            const locked = isStepLocked(step);
            const completed = completedSet.has(step.id);
            return (
              <button
                type="button"
                key={step.id}
                className={`roadmap-module-card ${locked ? 'locked' : ''} ${completed ? 'completed' : ''} ${
                  selectedStep.id === step.id ? 'active' : ''
                }`}
                onClick={() => setSelectedStepId(step.id)}
                aria-pressed={selectedStep.id === step.id}
              >
                <span className="roadmap-module-main">
                  <span className="roadmap-module-number">{step.moduleLabel}</span>
                  <span className="roadmap-module-phase">{step.phase}</span>
                  <span className="roadmap-module-title">{step.title}</span>
                  <span className="roadmap-module-description">{step.shortDescription}</span>
                  <span className="roadmap-module-meta">
                    {step.isFree ? (
                      'Free'
                    ) : locked ? (
                      <>
                        <span className="roadmap-lock-icon" aria-hidden>
                          <svg viewBox="0 0 24 24" focusable="false">
                            <path d="M17 10h-1V8a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2Zm-7-2a2 2 0 1 1 4 0v2h-4V8Zm7 11H7v-7h10v7Z" />
                          </svg>
                        </span>
                        Locked - Pro
                      </>
                    ) : (
                      'Pro'
                    )}
                  </span>
                </span>
                <span className="roadmap-module-icon-wrap">
                  <RoadmapModuleVisual stepId={step.id} logoUrl={step.logoUrl} />
                </span>
              </button>
            );
          })}
        </section>

        <section className="roadmap-detail-card" aria-label="Selected roadmap step">
          <div className="roadmap-detail-header">
            <div>
              <h2>{selectedStep.title}</h2>
              <p>{selectedStep.detail}</p>
            </div>
            <span
              className={`roadmap-pill ${selectedStep.isFree ? 'free' : hasProAccess ? 'pro' : 'locked'}`}
            >
              {selectedStep.isFree
                ? 'Free Step'
                : hasProAccess
                  ? 'Pro Step'
                  : 'Locked Step'}
            </span>
          </div>

          <ul className="roadmap-task-list">
            {selectedStep.tasks.map((task) => (
              <li key={task}>{task}</li>
            ))}
          </ul>

          <div className="roadmap-detail-actions">
            <button
              type="button"
              className="roadmap-complete-button"
              disabled={isStepLocked(selectedStep)}
              onClick={() => toggleCompleted(selectedStep.id)}
            >
              {completedSet.has(selectedStep.id) ? 'Mark as not completed' : 'Mark as completed'}
            </button>
            {isStepLocked(selectedStep) && (
              <Link to="/signup" className="roadmap-upgrade-link">
                Unlock Pro Roadmap
              </Link>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default Roadmap;
